// src/admin/screens/loans/LoansQueueScreen.tsx
// The hottest admin surface: all loans in one filterable queue. Lays out as
// SectionHead + search + status chip row + DataTable, matching the design.
//
// Filter chips and the search box mutate ?status= / ?q= in the URL so reloads
// are sticky. Clicking a row navigates to /loans/:id. Queue is capped at 200
// rows from the hook — narrow the filter if you need more.
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Download } from 'lucide-react';
import { useLoans, type LoanRow } from '@admin/hooks/useLoans';
import DataTable, { type Column } from '@admin/components/DataTable';
import FilterChip from '@admin/components/FilterChip';
import StatusChip from '@admin/components/StatusChip';
import Money from '@admin/components/Money';
import SectionHead from '@admin/components/SectionHead';
import Button from '@admin/components/Button';
import { LOAN_STATUS_LABEL, LOAN_STATUS_TONE } from '@admin/utils/loanStatus';
import type { Database } from '@/lib/database.types';
import type { Ngwee } from '@/lib/types';
import { formatCatDate } from '@admin/utils/dates';
import styles from './LoansQueueScreen.module.css';

type LoanStatus = Database['public']['Enums']['loan_status'];

const STATUSES: LoanStatus[] = [
  'pending_approval',
  'approved',
  'disbursement_pending',
  'active',
  'delinquent',
  'default',
  'repaid',
  'cancelled',
  'disbursement_failed',
  'restructured',
];

function borrowerName(r: LoanRow): string {
  return r.profiles?.display_name ?? r.profiles?.full_name ?? '—';
}

export default function LoansQueueScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const rawStatus = params.get('status');
  const status = (rawStatus && (STATUSES as string[]).includes(rawStatus)
    ? (rawStatus as LoanStatus)
    : 'all') as LoanStatus | 'all';
  const search = params.get('q') ?? '';

  const { data, isLoading, error } = useLoans({ status, search });
  const rowCount = data?.length ?? 0;

  const cols: Column<LoanRow>[] = useMemo(
    () => [
      {
        key: 'loan_number',
        header: 'Reference',
        render: (r) => (
          <span className="mono" style={{ fontWeight: 600, color: 'var(--c-p800)' }}>{r.loan_number}</span>
        ),
        width: '130px',
      },
      {
        key: 'borrower',
        header: 'Borrower',
        render: (r) => <span style={{ fontWeight: 600 }}>{borrowerName(r)}</span>,
      },
      {
        key: 'principal',
        header: 'Principal',
        render: (r) => <Money amount={r.principal_ngwee as Ngwee} />,
        align: 'right',
      },
      {
        key: 'total',
        header: 'Total',
        render: (r) =>
          r.total_repayable_ngwee != null
            ? <Money amount={r.total_repayable_ngwee as Ngwee} />
            : <span style={{ color: 'var(--c-text-dim)' }}>—</span>,
        align: 'right',
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => (
          <StatusChip label={LOAN_STATUS_LABEL[r.status]} tone={LOAN_STATUS_TONE[r.status]} />
        ),
      },
      {
        key: 'due',
        header: 'Due',
        render: (r) => r.due_date
          ? formatCatDate(r.due_date)
          : <span style={{ color: 'var(--c-text-dim)' }}>—</span>,
      },
      {
        key: 'applied',
        header: 'Submitted',
        render: (r) => (
          <span style={{ color: 'var(--c-text-muted)', fontSize: 12.5 }}>
            {formatCatDate(r.applied_at)}
          </span>
        ),
      },
    ],
    [],
  );

  const setParam = (name: string, value: string | null): void => {
    const next = new URLSearchParams(params);
    if (value == null || value === '') next.delete(name);
    else next.set(name, value);
    setParams(next, { replace: true });
  };

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Operations"
        title="Loan applications"
        description={`${rowCount} loan${rowCount === 1 ? '' : 's'}${status !== 'all' ? ` · ${LOAN_STATUS_LABEL[status]}` : ''}`}
        action={<Button variant="secondary" size="sm" leftIcon={<Download size={14} />}>Export CSV</Button>}
      />

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Search size={15} />
          <input
            className={styles.search}
            placeholder="Search loan # or borrower name…"
            value={search}
            onChange={(e) => setParam('q', e.target.value)}
            aria-label="Search loans"
          />
        </div>
      </div>

      <div className={styles.chipsRow}>
        <FilterChip label="All" active={status === 'all'} onClick={() => setParam('status', null)} />
        {STATUSES.map((s) => (
          <FilterChip
            key={s}
            label={LOAN_STATUS_LABEL[s]}
            active={status === s}
            onClick={() => setParam('status', s)}
          />
        ))}
        <span className={styles.resultCount}>{rowCount} result{rowCount === 1 ? '' : 's'}</span>
      </div>

      <DataTable
        rows={data}
        columns={cols}
        isLoading={isLoading}
        error={(error as Error | null)?.message ?? null}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/loans/${r.id}`)}
        emptyLabel="No loans match that filter."
      />
    </div>
  );
}
